/**
 * Locale selection for the user app.
 *
 * Phase 0.5 of the Spanish rollout (`docs/plans/spanish-translation/`). This is the plumbing only:
 * it records what language the user wants and sends it to the API. Nothing here translates
 * anything, and no translation service is called from the browser.
 *
 * What currently responds to it: ZigBot, which generates its replies in the selected language.
 * Static interface copy and database content stay English until the later phases land, so a
 * Spanish session today is a Spanish chatbot inside an English interface. That is deliberate,
 * and it is the cheapest way to prove the locale path end to end.
 *
 * DARK LAUNCH. Nothing here selects a locale on the user's behalf. Only an explicit choice, made
 * through the flag-gated LanguageSwitcher and stored below, moves the app off English — see
 * {@link getLocale} for why the browser preference is deliberately not consulted yet.
 */

export const SUPPORTED_LOCALES = ['en', 'es'] as const;

export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

export const DEFAULT_LOCALE: SupportedLocale = 'en';

/** Label for each locale, written in that locale. Never translate these. */
export const LOCALE_LABELS: Record<SupportedLocale, string> = {
    en: 'English',
    es: 'Español',
};

/** Where the choice used to live, before the cookie. Read once to carry an existing choice over, then removed. */
const LEGACY_STORAGE_KEY = 'gozig.locale';

function isSupported(value: string | null | undefined): value is SupportedLocale {
    return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * The active locale: an explicit stored choice, otherwise English.
 *
 * The choice lives in one place, the {@link LOCALE_COOKIE}, because the server needs it too: it sends an
 * English public URL to its `/es` version (server/rsc.ts) and sets the cookie itself when the footer switch
 * is used (`?lang=`). A second copy in the browser could disagree with it, and did.
 *
 * The browser preference is deliberately NOT consulted here while the rollout is dark. It used to
 * be, and that made Spanish opt-out rather than opt-in: every visitor whose browser is set to
 * Spanish would have been switched automatically on merge, with no flag involved and no way to
 * refuse, because the only control that sets a locale is hidden behind
 * FEATURE_FLAG_SPANISH_TRANSLATION. That reaches further than it sounds: every API call carries
 * this value (`?locale=`), and `i18n.ts` fetches a catalogue for it at boot.
 *
 * {@link getBrowserPreferredLocale} still holds that logic. Call it from the switcher to seed a
 * first-time default once the flag is on; until then the only way into Spanish is choosing it.
 *
 * Note this is per-browser. Once the user profile carries a locale, that becomes the source of
 * truth and this drops back to a first-paint hint so the UI does not flash the wrong language
 * while the profile loads.
 */
export function getLocale(): SupportedLocale {
    return readLocaleCookie() ?? migrateLegacyChoice() ?? DEFAULT_LOCALE;
}

/**
 * A choice made before the cookie existed, moved into the cookie. Storage throws in private windows and when
 * site data is blocked, so a failure reads as no choice (English).
 */
function migrateLegacyChoice(): SupportedLocale | null {
    try {
        const stored = window.localStorage.getItem(LEGACY_STORAGE_KEY);
        if (!isSupported(stored)) return null;
        setLocale(stored);
        return stored;
    }
    catch {
        return null;
    }
}

/**
 * The locale the browser says the user prefers, or English.
 *
 * Not consulted by {@link getLocale} on purpose (see there). This exists so that whoever turns
 * FEATURE_FLAG_SPANISH_TRANSLATION on has the auto-detection ready to wire into the switcher as a
 * first-visit default, behind the flag, rather than reinventing it.
 */
export function getBrowserPreferredLocale(): SupportedLocale {
    const browserPrimary = navigator.language?.split('-')[0];
    return isSupported(browserPrimary) ? browserPrimary : DEFAULT_LOCALE;
}

/**
 * The reader's language choice. Read by the SPA ({@link getLocale}) and by the server, which sends an English
 * public URL to its `/es` version when this says Spanish and sets it itself for the footer switch on
 * server-rendered pages (server/rsc.ts). Set only by an explicit switch, never by visiting a `/es` link
 * someone shared.
 */
export const LOCALE_COOKIE = 'gozig_locale';

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function readLocaleCookie(): SupportedLocale | null {
    try {
        const match = new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([a-z]{2})(?:;|$)`).exec(document.cookie);
        return match && isSupported(match[1]) ? match[1] : null;
    }
    catch {
        return null;
    }
}

/**
 * Persist the user's choice. Returns false when the cookie could not be written (cookies blocked), so callers
 * can decide; the switch still applies to the page in front of them.
 */
export function setLocale(locale: SupportedLocale): boolean {
    try {
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
    }
    catch {
        return false;
    }
    try {
        window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
    catch {
        // Storage unavailable: there is nothing in it to remove either.
    }
    return readLocaleCookie() === locale;
}

/**
 * A non-English language prefix on a path, for the SPA: `/es/dashboard` → `{ locale: 'es', path: '/dashboard' }`.
 * The server hands signed-in visitors to the SPA even on `/es` URLs, and the SPA's routes have no prefix.
 */
export function splitLocalePrefix(pathname: string): { locale: SupportedLocale; path: string } | null {
    const match = /^\/([a-z]{2})(\/.*)?$/.exec(pathname);
    if (!match || match[1] === DEFAULT_LOCALE || !isSupported(match[1])) return null;
    return { locale: match[1], path: match[2] || '/' };
}

/** Reflect the locale on <html lang> so screen readers announce content in the right language. */
export function applyDocumentLanguage(locale: SupportedLocale): void {
    document.documentElement.lang = locale;
}

/**
 * Whether the SPA should leave public-page links (home, FAQs, legal pages) to the browser instead of routing
 * them itself: for a signed-out reader who chose a language other than English. A full load lets the server
 * send them to that page's URL in their language (`/` → `/es`), where it is server-rendered; routed inside
 * the SPA they would see the SPA's copy at the English URL, which a reload then shows in English.
 */
export function shouldReloadForLocale(): boolean {
    if (getLocale() === DEFAULT_LOCALE) return false;
    try {
        return !/(?:^|;\s*)sessioncache=/.test(document.cookie);
    }
    catch {
        return false;
    }
}
